<script setup>
import { ref, computed } from 'vue';
import RatingReady from '@/Components/RatingReady.vue';
import axios from 'axios';

const props = defineProps({
  reviews: { type: Array, default: () => [] },
  meta: { type: Object, default: () => ({}) },
});

const list = ref([...props.reviews]);
const page = ref(1);
const loading = ref(false);
const total = ref(props.meta.total || props.reviews.length);
const active = ref(null);

const hasMore = computed(() => list.value.length < total.value);

const fmtDate = (d) => {
  if (!d) return '';
  try { return new Date(String(d).replace(' ', 'T')).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }); }
  catch { return ''; }
};

async function loadMore() {
  if (loading.value || !hasMore.value) return;
  loading.value = true;
  try {
    const { data } = await axios.get(route('usluga.reviews.api'), {
      params: { main: props.meta.main, second: props.meta.second, city: props.meta.city, geo: props.meta.geo, page: page.value + 1 },
    });
    list.value.push(...(data.data || []));
    page.value += 1;
    if (typeof data.total === 'number') total.value = data.total;
  } catch (e) { /* тихо игнорируем */ }
  loading.value = false;
}

function open(rev) {
  active.value = rev;
  if (typeof document !== 'undefined') document.body.style.overflow = 'hidden';
}
function close() {
  active.value = null;
  if (typeof document !== 'undefined') document.body.style.overflow = '';
}
</script>

<template>
  <section v-if="list.length" id="reviews" class="bg-white scroll-mt-24">
    <div class="max-w-7xl mx-auto px-4 py-8 md:py-12">
      <div class="flex items-baseline gap-2 mb-5">
        <h2 class="text-2xl lg:text-3xl font-extrabold tracking-tight text-gray-900">Отзывы клиентов</h2>
        <span v-if="total" class="text-base font-medium text-gray-500">{{ total }}</span>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <button
          v-for="rev in list"
          :key="rev.id"
          type="button"
          @click="open(rev)"
          class="text-left flex flex-col gap-2 rounded-xl border border-gray-200 bg-white p-4 hover:border-indigo-300 hover:shadow-sm transition-all"
        >
          <div class="flex items-start justify-between gap-2">
            <span class="font-semibold text-gray-900 text-sm truncate min-w-0">{{ rev.fio }}</span>
            <span class="shrink-0 flex items-center gap-1">
              <RatingReady :rating="Number(rev.rating)" :reviewRating="false" />
              <span class="text-xs font-semibold text-gray-700 tabular-nums">{{ Number(rev.rating).toFixed(1) }}</span>
            </span>
          </div>
          <p class="text-sm text-gray-600 leading-relaxed line-clamp-4">{{ rev.description }}</p>
          <div class="mt-auto flex items-center justify-between gap-3 pt-1 text-xs text-gray-500">
            <span class="shrink-0">{{ fmtDate(rev.created_at) }}</span>
            <span v-if="rev.lawyer_name" class="truncate min-w-0 text-right">юрист: {{ rev.lawyer_name }}</span>
          </div>
        </button>
      </div>

      <div v-if="hasMore" class="mt-6 text-center">
        <button
          type="button"
          @click="loadMore"
          :disabled="loading"
          class="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 hover:border-indigo-300 hover:text-indigo-700 disabled:opacity-60 transition-colors"
        >
          <svg v-if="loading" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          {{ loading ? 'Загрузка…' : 'Показать ещё' }}
        </button>
      </div>
    </div>

    <!-- Модальное окно отзыва -->
    <div v-if="active" class="fixed inset-0 z-50 flex items-center justify-center p-4" @click.self="close">
      <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="close"></div>
      <div class="relative bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col overflow-hidden">
        <div class="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100">
          <div class="min-w-0">
            <p class="font-semibold text-gray-900 truncate">{{ active.fio }}</p>
            <div class="mt-1 flex items-center gap-1.5">
              <RatingReady :rating="Number(active.rating)" :reviewRating="false" />
              <span class="text-sm font-semibold text-gray-700 tabular-nums">{{ Number(active.rating).toFixed(1) }}</span>
            </div>
          </div>
          <button @click="close" aria-label="Закрыть"
            class="shrink-0 p-1 rounded-lg text-gray-500 hover:text-gray-600 hover:bg-gray-100 transition-colors">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="overflow-y-auto px-5 py-4 flex-1">
          <p class="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{{ active.description }}</p>
        </div>
        <div class="flex items-center justify-between gap-2 px-5 py-3 border-t border-gray-100 text-xs text-gray-500">
          <span>{{ fmtDate(active.created_at) }}</span>
          <span v-if="active.lawyer_name" class="truncate text-right">юрист: {{ active.lawyer_name }}</span>
        </div>
      </div>
    </div>
  </section>
</template>
